import { URI_PREFIX, PLAY_HTTP_PREFIX, PLAY_HTTPS_PREFIX, OPEN_HTTP_PREFIX, OPEN_HTTPS_PREFIX, PATH_PREFIX, } from '../enums/prefix';
import { URIFormat } from '../enums/uri_format';
import { URITypeMap } from '../enums/uri_type';
import { URIParsers } from './uri_definitions/parsers';
import { matchLocale } from './helpers';
function decodeQueryString(str) {
    return str.split('&').reduce((object, pair) => {
        const [key, value] = pair.split('=');
        if (key && value) {
            object[key] = decodeURIComponent(value);
        }
        return object;
    }, {});
}
/**
 * Split an string URI or HTTP/HTTPS URL into components, skipping the prefix.
 *
 * @param strURI - A string URI to split.
 * @returns SplitURI The parsed URI.
 */
export function splitIntoComponents(strURI) {
    let components;
    let format;
    let query;
    let anchor;
    let str = strURI;
    const [_str, _params] = str.split('?');
    if (_str && _params) {
        str = _str;
        const [_query, _anchor] = _params.split('#');
        if (_query) {
            query = decodeQueryString(_query);
            anchor = _anchor;
        }
    }
    const hashSplit = str.split('#');
    if (hashSplit.length > 1) {
        // first token
        str = hashSplit.shift();
        // last token
        anchor = hashSplit.pop();
    }
    if (str.indexOf(URI_PREFIX) === 0) {
        components = str.slice(URI_PREFIX.length).split(':');
        format = URIFormat.URI;
    }
    else {
        // For HTTP URLs, ignore any query string argument
        const [tempStr] = str.split('?');
        if ((tempStr === null || tempStr === void 0 ? void 0 : tempStr.indexOf(PLAY_HTTP_PREFIX)) === 0) {
            components = tempStr.slice(PLAY_HTTP_PREFIX.length).split('/');
        }
        else if ((tempStr === null || tempStr === void 0 ? void 0 : tempStr.indexOf(PLAY_HTTPS_PREFIX)) === 0) {
            components = tempStr.slice(PLAY_HTTPS_PREFIX.length).split('/');
        }
        else if ((tempStr === null || tempStr === void 0 ? void 0 : tempStr.indexOf(OPEN_HTTP_PREFIX)) === 0) {
            components = tempStr.slice(OPEN_HTTP_PREFIX.length).split('/');
        }
        else if ((tempStr === null || tempStr === void 0 ? void 0 : tempStr.indexOf(OPEN_HTTPS_PREFIX)) === 0) {
            components = tempStr.slice(OPEN_HTTPS_PREFIX.length).split('/');
        }
        else if ((tempStr === null || tempStr === void 0 ? void 0 : tempStr.indexOf(PATH_PREFIX)) === 0) {
            components = tempStr.slice(PATH_PREFIX.length).split('/');
        }
        else {
            throw new TypeError('Invalid Spotify URI!');
        }
        format = URIFormat.URL;
    }
    if (anchor) {
        components.push(anchor);
    }
    return {
        format: format,
        components: components,
        query: query,
    };
}
/**
 * Parses the components of a URI into a real URI object.
 *
 * @param components - The components of the URI as a string array.
 * @param format - The format of the source string.
 * @param query - The query object.
 * @param parseUnknown - Should we handle an unknown URI type.
 * @returns The URI object.
 */
export function parseFromComponents(components, format, query, parseUnknown) {
    let _current = 0;
    const _getNextComponent = () => {
        const component = components[_current++];
        // Check that the URI component does not contain spaces.
        // <https://ghe.spotify.net/java/uri/blob/968046d026dcbe0a3d91dd11128005b2656a87d8/src/main/java/com/spotify/common/uri/SpotifyUriParser.java#L36>
        if (component && /\s/.test(component)) {
            throw new TypeError('Invalid Spotify URI!');
        }
        return component;
    };
    const _getNextRequiredComponent = () => {
        const component = _getNextComponent();
        if (!component) {
            throw new TypeError('Invalid Spotify URI!');
        }
        return component;
    };
    const _getIdComponent = () => {
        const component = _getNextRequiredComponent();
        if (component.length > 22) {
            throw new TypeError('Invalid Spotify URI!');
        }
        return component;
    };
    const _getHexIdComponent = (length) => {
        const component = _getNextRequiredComponent();
        if (component.length !== length) {
            throw new TypeError('Invalid Spotify URI!');
        }
        if (!/^[a-fA-F0-9]+$/.test(component)) {
            throw new Error('Invalid Hex ID');
        }
        return component;
    };
    const _getBase62IdComponent = () => {
        const component = _getNextRequiredComponent();
        if (component.length !== 22) {
            throw new TypeError('Invalid Spotify URI!');
        }
        // Check that the supposedly base62 encoded ID only contains valid characters.
        // <https://ghe.spotify.net/java/uri/blob/master/src/main/java/com/spotify/common/uri/Base62.java#L26-L34>
        if (!/^[a-zA-Z0-9]+$/.test(component)) {
            throw new Error('Invalid ID');
        }
        return component;
    };
    const _getRemainingComponents = () => {
        return components.slice(_current);
    };
    const _getRemainingString = () => {
        const separator = format === URIFormat.URI ? ':' : '/';
        return components.slice(_current).join(separator);
    };
    const parsingContext = {
        getAllComponents: () => components,
        getQueryParams: () => query,
        getNextComponent: _getNextComponent,
        getNextRequiredComponent: _getNextRequiredComponent,
        getIdComponent: _getIdComponent,
        getHexIdComponent: _getHexIdComponent,
        getBase62IdComponent: _getBase62IdComponent,
        getRemainingComponents: _getRemainingComponents,
        getRemainingString: _getRemainingString,
    };
    let component = _getNextRequiredComponent();
    let uriParser = URIParsers[component];
    if (!uriParser) {
        const isLocaleBasedURI = matchLocale(component);
        if (isLocaleBasedURI) {
            component = _getNextRequiredComponent();
            uriParser = URIParsers[component];
        }
    }
    if (uriParser) {
        return uriParser(parsingContext, format, {});
    }
    if (parseUnknown) {
        return URIParsers[URITypeMap.UNKNOWN](parsingContext, format, {});
    }
    throw new TypeError('Invalid or unknown Spotify URI!');
}
export function parse(uri, parseUnknown) {
    const components = splitIntoComponents(uri);
    return parseFromComponents(components.components, components.format, components.query, parseUnknown);
}
//# sourceMappingURL=parser.js.map